<template>
  <div class="form-preview">
    <div v-if="loading" class="fh center">
      <loading-circle />
    </div>
    <div v-else class="form-preview-autogenerated">
      <template v-for="(ele, index) in formTemplateBody">
        <div :key="`auto-${index}`" v-if="ele.autogenerated && ele.caption !== 'Project Id'">
          <span class="label">{{ ele.caption }}:</span>
          <span class="value">--</span>
        </div>
      </template>
    </div>

    <div class="form-preview-body">
      <template v-for="(ele, index) in formTemplateBody">
        <component
          v-if="!ele.autogenerated"
          :key="index"
          :is="ele.key + '_COMPONENT'"
          :data="ele"
          :viewOnly="true"
        />
      </template>
    </div>
  </div>
</template>

<script>
import timeComponent from '@/components/form/elements/timeComponent.vue'
import configurationList from './elements/configurationList.vue'
import fileComponent from './elements/fileComponent.vue'
import dateComponent from '@/components/form/elements/dateComponent.vue'
import longTextComponent from '@/components/form/elements/longTextComponent.vue'
import numberComponent from '@/components/form/elements/numberComponent.vue'
import switchComponent from '@/components/form/elements/switchComponent.vue'
import textComponent from '@/components/form/elements/textComponent.vue'
import companyComponent from '@/components/form/elements/companyComponent.vue'
import userComponent from '@/components/form/elements/userComponent.vue'
import multiUserComponent from '@/components/form/elements/multiUserComponent.vue'
import TagsComponent from '@/components/form/elements/tagsComponent.vue'
import materialComponent from './elements/materialComponent.vue'
import locationComponent from './elements/locationComponent.vue'
import productCodeComponent from './elements/productCodeComponent.vue'
import documentsComponent from './elements/documentsComponent.vue'
import bomComponent from './elements/bomComponent.vue'
import loadingCircle from '../common/loadingCircle.vue'

export default {
  name: 'FormPreview',
  components: {
    BOOLEAN_COMPONENT: switchComponent,
    NUMBER_COMPONENT: numberComponent,
    TEXT_COMPONENT: textComponent,
    LONG_TEXT_COMPONENT: longTextComponent,
    DATE_COMPONENT: dateComponent,
    TIME_COMPONENT: timeComponent,
    CONFIGURATION_LIST_COMPONENT: configurationList,
    ATTACHMENT_COMPONENT: fileComponent,
    COMPANY_COMPONENT: companyComponent,
    USER_COMPONENT: userComponent,
    MULTI_USER_COMPONENT: multiUserComponent,
    MATERIALS_COMPONENT: materialComponent,
    LOCATION_COMPONENT: locationComponent,
    PRODUCT_CODE_COMPONENT: productCodeComponent,
    BOM_COMPONENT: bomComponent,
    TAGS_COMPONENT: TagsComponent,
    DOCUMENTS_COMPONENT: documentsComponent,
    loadingCircle
  },
  props: {
    formName: {
      type: String
    },
    loading: {
      type: Boolean
    },
    formType: {
      type: String
    },
    formTemplateBody: {
      type: Array
    }
  },
  data () {
    return {}
  }
}
</script>

<style lang="scss" scoped>
/* Main preview container with modal-like appearance */
.form-preview {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #f8f9fa;
  overflow-y: auto;
  overflow-x: hidden;
  max-width: 100%;
}

/* Header - clean white background */
.form-preview-header {
  background: #ffffff;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e8e8e8;
}

.form-preview-header-title {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &-name {
    font-size: var(--text-2xl);
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 0;
  }

  &-type {
    font-size: var(--text-base);
    font-weight: 400;
    color: #6b7280;
  }
}

.form-preview-autogenerated {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 10px 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.form-preview-autogenerated > div {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex: 1 1 0;
  min-width: 0;
}

.form-preview-autogenerated .label {
  font-size: var(--text-xs2);
  font-weight: 500;
  color: #374151;
  flex-shrink: 0;
}

.form-preview-autogenerated .value {
  font-size: var(--text-xs2);
  font-weight: 400;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Form body - white background, scrollable */
.form-preview-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0 15px 15px;
  background: #f8f9fa;
  overflow: visible;
}

/* Form fields styling */
::v-deep .form-field,
::v-deep .field-wrapper,
::v-deep .form-element,
::v-deep .form-control {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  margin-bottom: 20px !important;
}

/* Labels */
::v-deep label {
  display: block;
  margin-bottom: 5px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: #374151 !important;
}

/* Inputs, selects, textareas - clean white design */
::v-deep input,
::v-deep select,
::v-deep textarea,
::v-deep .custom-dropdown,
::v-deep .tags-input {
  width: 100% !important;
  background: #ffffff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 6px !important;
  padding: 10px 14px !important;
  font-size: 14px !important;
  color: #1f2937 !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
  transition: border-color .15s, box-shadow .15s;
}

::v-deep input:focus,
::v-deep select:focus,
::v-deep textarea:focus {
  border-color: #9ca3af !important;
  box-shadow: 0 0 0 3px rgba(156, 163, 175, 0.1) !important;
  outline: none !important;
}

::v-deep ::placeholder {
  color: #9ca3af !important;
  font-size: 11px !important;
}

::v-deep textarea {
  min-height: 100px !important;
  resize: vertical !important;
}

/* Empty placeholder */
::v-deep .empty-placeholder {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  color: #9ca3af !important;
  padding: 16px !important;
  border-radius: 6px !important;
  text-align: center;
  font-size: 14px !important;
}

/* Scrollbar styling */
.form-preview-body::-webkit-scrollbar {
  width: 8px;
}

.form-preview-body::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.form-preview-body::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.form-preview-body::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Small screens */
@media (max-width: 720px) {
  .form-preview-header {
    padding: 16px 20px 12px;
  }

  .form-preview-autogenerated {
    flex-direction: column;
    align-items: stretch;
    margin: 12px 20px;
    padding: 12px 16px;
    gap: 8px;
  }
  .form-preview-autogenerated > div {
    flex: none;
    width: 100%;
  }

  .form-preview-body {
    padding: 0 20px 20px;
  }
}
</style>
